import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, of } from 'rxjs';

import { MenuModel } from '../../models/menu/menu.model';
import { PermissionService } from './permission.service';

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  private menuItemsSubject = new BehaviorSubject<MenuModel[]>([]);
  readonly menuItems$: Observable<MenuModel[]> = this.menuItemsSubject.asObservable();
  private menuInitialized = false;

  constructor(
    private permissionService: PermissionService
  ) {
    this.permissionService.permissions$.subscribe(permissions => {
      if (permissions) {
        this.updateMenuItems();
      }
    });

    this.permissionService.userRole$.subscribe(_ => {
      if (this.permissionService.isAdminRole()) {
        this.updateMenuItems();
      }
    });

    this.initializeMenu();
  }

  /**
   * Inicializa o menu de forma reativa, aguardando o carregamento das permissões
   */
  initializeMenu(): void {
    if (this.menuInitialized) {
      return;
    }

    this.permissionService.ensureRoleLoaded()
      .pipe(
        catchError(error => {
          console.error('Erro ao inicializar menu:', error);
          this.menuItemsSubject.next([]);
          return of(null);
        })
      )
      .subscribe(_ => {
        if (this.permissionService.isAdminRole()) {
          this.updateMenuItems();
          this.menuInitialized = true;
          return;
        }

        this.permissionService.ensurePermissionsLoaded()
          .pipe(
            catchError(error => {
              console.error('Erro ao carregar permissões:', error);
              this.menuItemsSubject.next([]);
              return of(null);
            })
          )
          .subscribe(permissions => {
            if (permissions) {
              this.updateMenuItems();
              this.menuInitialized = true;
            } else {
              this.menuItemsSubject.next([]);
            }
          });
      });
  }

  /**
   * Atualiza os itens de menu com base nas permissões atuais
   */
  private updateMenuItems(): void {
    const menu = this.buildMenuStructure();
    this.processMenuVisibility(menu);
    this.menuItemsSubject.next(menu.filter(item => !!item.isVisible));
  }

  /**
   * Retorna os itens de menu atuais
   * @returns Array de MenuModel
   */
  getMenuItems(): MenuModel[] {
    return this.menuItemsSubject.getValue();
  }

  /**
   * Constrói a estrutura de menu completa
   * @returns Array de MenuModel com a estrutura completa
   */
  private buildMenuStructure(): MenuModel[] {
    return [
      {
        id: 'dropdown-home',
        nameMainItemMenu: 'Home',
        isHovered: false,
        url: '/home',
        permissionTarget: 'home',
        icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />`,
      },
      {
        id: 'dropdown-list-users',
        nameMainItemMenu: 'Usuários',
        isHovered: false,
        permissionTarget: 'list-users',
        icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />`,
        subItemMenu: [
          {
            name: 'Listar usuários',
            url: '/list-users',
            description: 'Gerencie usuários: visualize, adicione, edite ou remova.',
            isHovered: false,
            permissionTarget: 'list-users'
          }
        ]
      },
      {
        id: 'dropdown-licenciados-users',
        nameMainItemMenu: 'Licenciados',
        isHovered: false,
        permissionTarget: 'licenciados',
        icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />`,
        subItemMenu: [
          {
            name: 'Listar grupos econômicos',
            url: '/list-economic-group',
            description: 'Gerencie grupos econômicos: visualize, adicione, edite ou remova.',
            isHovered: false,
            permissionTarget: 'list-economic-group'
          },
          {
            name: 'Análise de mercado farmacêutico',
            url: '/pharmaceutical-market-analysis',
            description: 'Análise de mercado farmacêutico: análise comparativa entre locais.',
            isHovered: false,
            permissionTarget: 'pharmaceutical-market-analysis'
          },
          {
            name: 'Ativação de Cliente',
            url: '/ativar-precificacao',
            description: 'Ativação de cliente Inova/Midas via CNPJ.',
            isHovered: false,
            permissionTarget: 'pharmaceutical-market-analysis'
          },
          // {
          //   name: 'Score',
          //   url: '/score',
          //   description: 'Consulta a score utilizando um CPF.',
          //   isHovered: false,
          //   permissionTarget: 'score'
          // },
          {
            name: 'Listar contratos',
            url: '/list-contracts',
            description: 'Listar contratos ativos.',
            isHovered: false,
            permissionTarget: 'list-contracts'
          },
        ],
      },
      {
        id: 'dashboards',
        nameMainItemMenu: 'Dashboards',
        isHovered: false,
        icon: '',
        permissionTarget: 'dashboards',
        subItemMenu: [
          {
            name: 'Aquisição | Formulários',
            url: '/dashboard/formularios',
            description: 'Dashboard de dados de captação de formulários',
            isHovered: false,
            permissionTarget: 'true'
          },
          {
            name: 'Cadastro | Informações de Lojas',
            url: '/dashboard/informacoes-da-rede',
            description: 'Dashboard de informações de lojas',
            isHovered: false,
            permissionTarget: 'informacoes-da-rede'
          },
          // {
          //   name: 'Comercial | Ações de Sellout',
          //   url: '/dashboard/trade-marketing',
          //   description: 'Análise de Ações de Sellout',
          //   isHovered: false,
          //   permissionTarget: 'trade-marketing'
          // },
          {
            name: 'Comercial | Aderência de Campanhas',
            url: '/dashboard/comparativo-comercial',
            description: 'Dashboard de aderência de campanhas',
            isHovered: false,
            permissionTarget: 'comparativo-comercial'
          },
          // {
          //   name: 'Comercial | Análise de Marca Própria [Suprahealth]',
          //   url: '/dashboard/marca-propria',
          //   description: 'Painel de Análise de Marca Própria SupraHealth',
          //   isHovered: false,
          //   permissionTarget: 'marca-propria'
          // },
          // {
          //   name: 'Comercial | Cálculo de Rebate',
          //   url: '/dashboard/historico-rebate',
          //   description: 'Dashboard de cálculo de rebate',
          //   isHovered: false,
          //   permissionTarget: 'historico-rebate'
          // },
          {
            name: 'Comercial | Crescimento IQVIA',
            url: '/dashboard/crescimento-iqva',
            description: 'Painel nível EAN de análise de crescimento IQVIA',
            isHovered: false,
            permissionTarget: 'true'
          },
          {
            name: 'Comercial | Crescimento IQVIA',
            url: '/dashboard/mapeamento-comercial-industria',
            description: 'Dashboard de crescimento IQVIA',
            isHovered: false,
            permissionTarget: 'mapeamento-comercial-industria'
          },
          {
            name: 'Comercial | Cumprimento de Metas Comerciais',
            url: '/dashboard/cumprimento-metas-comercial',
            description: 'Painel de Análise de Cumprimento de Metas Comerciais',
            isHovered: false,
            permissionTarget: 'cumprimento-metas-comercial'
          },
          {
            name: 'Comercial | Histórico de Crescimento IQVIA',
            url: '/dashboard/historico-crescimento-iqvia',
            description: 'Painel de histórico de crescimento no IQVIA',
            isHovered: false,
            permissionTarget: 'true'
          },
          {
            name: 'Comercial | Mapeamento ACODE',
            url: '/dashboard/mapeamento-comercial',
            description: 'Dashboard de mapeamento comercial ACODE',
            isHovered: false,
            permissionTarget: 'mapeamento-comercial'
          },
          // {
          //   name: 'Comercial | Mapeamento de Lojas',
          //   url: '/dashboard/mapeamento-loja',
          //   description: 'Painel de Análise de Mapemaneto de Lojas',
          //   isHovered: false,
          //   permissionTarget: 'true'
          // },
          // {
          //   name: 'Comercial | Metas de Vendas [consultores]',
          //   url: '/dashboard/metas-vendas',
          //   description: 'Painel de Metas de Vendas dos consultores',
          //   isHovered: false,
          //   permissionTarget: 'metas-vendas'
          // },
          // {
          //   name: 'Comercial | Montagem de Pedido',
          //   url: '/dashboard/montagem-pedido',
          //   description: 'Painel de Análise de montagem de Pedidos',
          //   isHovered: false,
          //   permissionTarget: 'true'
          // },
          // {
          //   name: 'Comercial | Ofertas Disponíveis',
          //   url: '/dashboard/ofertas-disponiveis',
          //   description: 'Painel de Análise de Ofertas Disponíveis',
          //   isHovered: false,
          //   permissionTarget: 'true'
          // },
          {
            name: 'Comercial | Status de Pedidos',
            url: '/dashboard/pedidos',
            description: 'Painel de análise de pedidos',
            isHovered: false,
            permissionTarget: 'pedidos'
          },
          {
            name: 'Jurídico | Status de contratos',
            url: '/dashboard/status-contratos',
            description: 'Status de contratos do Grupo Hiper Saúde',
            isHovered: false,
            permissionTarget: 'true',
            allowedEmails: ["giuliana.nascimento@hipersaude.com.br", "edson.tsuka@hipersaude.com.br"]
          },
          {
            name: 'Financeiro | Inadimplência',
            url: '/dashboard/inadimplencia',
            description: 'Dashboard de inadimplencias',
            isHovered: false,
            permissionTarget: 'inadimplencia'
          },
          // {
          //   name: 'Marketing | Campanha Fórmula do Sucesso',
          //   url: '/dashboard/fidelizacao',
          //   description: 'Painel de programa de fidelização.',
          //   isHovered: false,
          //   permissionTarget: 'fidelizacao'
          // },
          // {
          //   name: 'Marketing | Eficiência de Campanhas',
          //   url: '/dashboard/eficiencia-campanhas',
          //   description: 'Análise de dados de eficiência de campanhas.',
          //   isHovered: false,
          //   permissionTarget: 'eficiencia-campanhas'
          // },
          // {
          //   name: 'Novos Negócios | Afiliados',
          //   url: '/dashboard/afiliados',
          //   description: 'Painel de afiliados',
          //   isHovered: false,
          //   permissionTarget: 'afiliados'
          // },
          {
            name: 'Novos Negócios | Análise de Economia Comercial',
            url: '/dashboard/analise-economia-comercial',
            description: 'Painel de análise de economia comercial',
            isHovered: false,
            permissionTarget: 'analise-economia-comercial'
          },
          {
            name: 'Novos Negócios | Mapa de Expansão',
            url: '/dashboard/mapa-de-expansao',
            description: 'Dashboard mapa de expansão',
            isHovered: false,
            permissionTarget: 'mapa-de-expansao'
          },
          {
            name: 'Novos Negócios | Meta de Lojas',
            url: '/dashboard/meta-de-expansao',
            description: 'Dashboard meta de lojas',
            isHovered: false,
            permissionTarget: 'meta-de-expansao'
          },
          {
            name: 'Operações | Catálogo de Produtos',
            url: '/dashboard/catalogo-produtos',
            description: 'Painel de Catalogo de Produtos',
            isHovered: false,
            permissionTarget: 'catalogo-produtos'
          },
          // {
          //   name: 'Operações | Churn',
          //   url: '/dashboard/churn',
          //   description: 'Dashboard de churn',
          //   isHovered: false,
          //   permissionTarget: 'churn'
          // },
          {
            name: 'Operações | Crescimento da Rede IQVIA',
            url: '/dashboard/crescimento-rede',
            description: 'Painel de Crescimento da Rede IQVIA',
            isHovered: false,
            permissionTarget: 'crescimento-rede'
          },
          {
            name: 'Operações | Crescimento de Indicadores',
            url: '/dashboard/crescimento-indicadores',
            description: 'Painel de Crescimento de Indicadores',
            isHovered: false,
            permissionTarget: 'crescimento-indicadores'
          },
          {
            name: 'Operações | Genéricos e Similares mais Vendidos',
            url: '/dashboard/formadores-de-opiniao',
            description: 'Dashboard de genéricos e similares mais vendidos',
            isHovered: false,
            permissionTarget: 'formadores-de-opiniao'
          },
          {
            name: 'Operações | Histórico de Produtos',
            url: '/dashboard/historico-produtos',
            description: 'Dashboard de histórico de cadastro de produtos',
            isHovered: false,
            permissionTarget: 'true'
          },
          {
            name: 'Operações | Painel de Usabilidade do Otimiza',
            url: '/dashboard/usabilidade-otimiza',
            description: 'Análise de dados de uso do Otimiza',
            isHovered: false,
            permissionTarget: 'usabilidade-otimiza'
          },
          // {
          //   name: 'Operações | Prontuário PBM',
          //   url: '/dashboard/pbm',
          //   description: 'Painel de PBM',
          //   isHovered: false,
          //   permissionTarget: 'pbm'
          // },
          {
            name: 'Operações | Sellout Geral',
            url: '/dashboard/sellout-geral',
            description: 'Dashboard de sellout geral',
            isHovered: false,
            permissionTarget: 'sellout-geral'
          },
          // {
          //   name: 'Operações | Semáforo',
          //   url: '/dashboard/semaforo',
          //   description: 'Dashboard de Semáforo',
          //   isHovered: false,
          //   permissionTarget: 'semaforo'
          // },
          {
            name: 'Operações | Status de Ativação MIDAS',
            url: '/dashboard/detalhamento-precificacao',
            description: 'Painel de análise de status de ativação MIDAS',
            isHovered: false,
            permissionTarget: 'true'
          },
          {
            name: 'Operações | Status de Captação [Pharmasync]',
            url: '/dashboard/status-de-captacao',
            description: 'Dashboard de status de captação do PharmaSync',
            isHovered: false,
            permissionTarget: 'status-de-captacao'
          },
          // {
          //   name: 'Rebate',
          //   url: '/dashboard/rebate',
          //   description: 'Painel de Rebate',
          //   isHovered: false,
          //   permissionTarget: 'rebate'
          // },
          // {
          //   name: 'Tech | Custo de Cloud',
          //   url: '/dashboard/custo-cloud',
          //   description: 'Painel de Análise do custo dos serviços Cloud',
          //   isHovered: false,
          //   permissionTarget: 'custo-cloud'
          // },
          {
            name: 'UDF | Mentoria',
            url: '/dashboard/mentoria',
            description: 'Dashboard de análise de Mentoria',
            isHovered: false,
            permissionTarget: 'mentoria'
          },
          // {
          //   name: 'Análise de Layoutização',
          //   url: '/dashboard/layoutizacao',
          //   description: 'Painel de Análise de Layoutização',
          //   isHovered: false,
          //   permissionTarget: 'layoutizacao'
          // },
          // {
          //   name: 'Análise de Vendas não Homologados',
          //   url: '/dashboard/analise-vendas-nao-homologados',
          //   description: 'Dashboard de análise de vendas não homologados',
          //   isHovered: false,
          //   permissionTarget: 'analise-vendas-nao-homologados'
          // },
          // {
          //   name: 'Análise Geográfica de Lojas',
          //   url: '/dashboard/analise-geografica-lojas',
          //   description: 'Dashboard de análise geográfica de lojas',
          //   isHovered: false,
          //   permissionTarget: 'analise-geografica-lojas'
          // },
          // {
          //   name: 'Analise de Produtividade',
          //   url: '/dashboard/analise-produtividade',
          //   description: 'Dashboard de analise de produtividade do time Hiper Saúde',
          //   isHovered: false,
          //   permissionTarget: 'analise-produtividade'
          // },
          // {
          //   name: 'Análise OS',
          //   url: '/dashboard/analise-os',
          //   description: 'Dashboard de analise OS',
          //   isHovered: false,
          //   permissionTarget: 'analise-os'
          // },
          // {
          //   name: 'Crescimento Industria',
          //   url: '/dashboard/crescimento-industria',
          //   description: 'Painel de crescimento industria',
          //   isHovered: false,
          //   permissionTarget: 'crescimento-industria'
          // },
          // {
          //   name: 'Demonstrativo de Resultados',
          //   url: '/dashboard/dre',
          //   description: 'Relatório de demonstrativo de resultados',
          //   isHovered: false,
          //   permissionTarget: 'dre'
          // },
          // {
          //   name: 'Força de vendas',
          //   url: '/dashboard/forca-de-vendas',
          //   description: 'Dashboard de força de vendas',
          //   isHovered: false,
          //   permissionTarget: 'forca-de-vendas'
          // },
          // {
          //   name: 'Integração de projetos',
          //   url: '/dashboard/integracao-projetos',
          //   description: 'Dashboard de integração de projetos',
          //   isHovered: false,
          //   permissionTarget: 'integracao-projetos'
          // },
          // {
          //   name: 'Licenciamento de Gestão',
          //   url: '/dashboard/licenciamento-gestao',
          //   description: 'Painel de Análise de Licenciamento de Gestão',
          //   isHovered: false,
          //   permissionTarget: 'licenciamento-gestao'
          // },
          // {
          //   name: 'Margem de contribuição',
          //   url: '/dashboard/margem-contribuicao',
          //   description: 'Dashboard de margem de contribuição',
          //   isHovered: false,
          //   permissionTarget: 'margem-contribuicao'
          // },
          // {
          //   name: 'Relatório gerencial',
          //   url: '/dashboard/relatorio-gerencial',
          //   description: 'Dashboard de relatório gerencial',
          //   isHovered: false,
          //   permissionTarget: 'relatorio-gerencial'
          // },
        ]
      },
      {
        id: 'products',
        nameMainItemMenu: 'Produtos',
        isHovered: false,
        icon: '',
        permissionTarget: 'products',
        subItemMenu: [
          {
            name: 'Gestão de produtos',
            url: '/product/search-by-product',
            description: 'Pesquise por EAN ou descrição do produto',
            isHovered: false,
            permissionTarget: 'search-by-product'
          }
        ]
      },
      {
        id: 'registers',
        nameMainItemMenu: 'Cadastros',
        isHovered: false,
        permissionTarget: 'registers',
        icon: '',
        subItemMenu: [
          {
            name: 'Cadastro de condição comercial',
            url: '/cadastro/condicao-comercial',
            description: 'Cadastre uma condição comercial',
            isHovered: false,
            permissionTarget: 'condicao-comercial'
          },
          {
            name: 'Cadastro de Clientes',
            url: '/cadastro/store-registration',
            description: 'Cadastre um Cliente Hiper Saúde',
            isHovered: false,
            permissionTarget: 'store-registration'
          },
          {
            name: 'Cadastro Precificação',
            url: '/ativar-precificacao',
            description: 'Cadastro Precificação Inova/Midas',
            isHovered: false,
            permissionTarget: 'pharmaceutical-market-analysis'
          }
        ]
      },
      {
        id: 'gerador-url',
        nameMainItemMenu: 'Gerador de URL',
        isHovered: false,
        permissionTarget: 'gerador-url',
        url: '/gerador-url',
        icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />`,
      },
      {
        id: 'gerencia-credenciais',
        nameMainItemMenu: 'Gerencia credenciais',
        isHovered: false,
        permissionTarget: 'gerencia-credenciais',
        url: '/gerencia-credenciais',
        icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />`,
      },
    ];
  }

  private processMenuVisibility(menu: MenuModel[]): void {
    const userEmail = this.permissionService.getUserEmail();

    menu.forEach(item => {
      if (item.allowedEmails && item.allowedEmails.length > 0) {
        if (!userEmail || !item.allowedEmails.includes(userEmail)) {
          item.isVisible = false;
          return;
        }
      }

      if (item.subItemMenu && item.subItemMenu.length > 0) {
        item.subItemMenu = item.subItemMenu.filter(subItem => {
          if (subItem.allowedEmails && subItem.allowedEmails.length > 0) {
            if (!userEmail || !subItem.allowedEmails.includes(userEmail)) {
              return false;
            }
          }

          if (!subItem.permissionTarget) return false;

          return this.permissionService.hasPagePermission(subItem.permissionTarget, 'read');
        });

        item.subItemMenu.forEach(subItem => {
          subItem.isVisible = true;
        });

        item.isVisible = item.subItemMenu.length > 0;
      } else {
        if (item.permissionTarget) {
          item.isVisible = this.permissionService.hasPagePermission(item.permissionTarget, 'read');
        } else {
          item.isVisible = true;
        }
      }
    });
  }
}
